import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import TimerIcon from "@mui/icons-material/Timer";
import ViewListIcon from "@mui/icons-material/ViewList";
import { partLabel } from "./partMeta";

/**
 * Lets the user choose how to take a public mock test:
 *   - "full": the complete test with a countdown + 990 TOEIC score.
 *   - "part": one or more selected parts, no countdown, per-part scoring.
 *
 * Calls `onStart({ mode, parts })` once a valid selection is made.
 */
const ModeSelectDialog = ({ open, exam, onClose, onStart }) => {
  const [mode, setMode] = useState("full");
  const [selectedParts, setSelectedParts] = useState([]);

  const availableParts = useMemo(
    () =>
      Array.isArray(exam?.availableParts)
        ? [...exam.availableParts].sort((a, b) => a - b)
        : [],
    [exam]
  );

  const togglePart = (partNumber) => {
    setSelectedParts((prev) =>
      prev.includes(partNumber)
        ? prev.filter((p) => p !== partNumber)
        : [...prev, partNumber].sort((a, b) => a - b)
    );
  };

  const canStart = mode === "full" || selectedParts.length > 0;

  const handleStart = () => {
    if (!canStart) return;
    onStart({
      mode,
      parts: mode === "part" ? selectedParts : [],
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {exam?.title || "Mock test"}
      </DialogTitle>
      <DialogContent dividers>
        {exam?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {exam.description}
          </Typography>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Choose how you want to practice
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, value) => value && setMode(value)}
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="full" sx={{ textTransform: "none", py: 1.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
              <TimerIcon />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Full test
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Countdown + 990 score
              </Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="part" sx={{ textTransform: "none", py: 1.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
              <ViewListIcon />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Practice by part
              </Typography>
              <Typography variant="caption" color="text.secondary">
                No timer · per-part score
              </Typography>
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === "part" && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select part(s) to practice
            </Typography>
            {availableParts.length === 0 ? (
              <Alert severity="info">No parts available for this test.</Alert>
            ) : (
              <FormGroup>
                {availableParts.map((partNumber) => (
                  <FormControlLabel
                    key={partNumber}
                    control={
                      <Checkbox
                        checked={selectedParts.includes(partNumber)}
                        onChange={() => togglePart(partNumber)}
                      />
                    }
                    label={partLabel(partNumber)}
                  />
                ))}
              </FormGroup>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleStart} disabled={!canStart}>
          Start
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModeSelectDialog;
